/**
 * BalanceDisplay — shows the authenticated user's wallet balance.
 * Auto-refreshes every 30 seconds. Intended for use in Header nav and any
 * other surface that needs a live balance chip.
 */
import { useEffect, useState } from 'react';
import { getBalance } from '../services/api';
import { formatZAR } from '../utils/currency';

interface BalanceDisplayProps {
  className?: string;
}

export default function BalanceDisplay({ className = '' }: BalanceDisplayProps) {
  // null = loading (not yet fetched), number = known value
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [error, setError] = useState(false);

  const refresh = async () => {
    try {
      const res = await getBalance();
      setBalanceCents(res.data.balance_cents);
      setError(false);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Show shimmer while the first fetch is in-flight
  if (balanceCents === null && !error) {
    return <span className={`shimmer inline-block h-4 w-16 rounded ${className}`} aria-label="Loading balance" />;
  }

  if (error || balanceCents === null) {
    return <span className={`text-text-muted ${className}`}>—</span>;
  }

  return <span className={className}>{formatZAR(balanceCents)}</span>;
}
